import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2, FiMapPin } from 'react-icons/fi';

const locations = [
  { value: 'hurghada_airport', label: 'Hurghada Airport (HRG)' },
  { value: 'hurghada_hotels', label: 'Hurghada Hotels' },
  { value: 'makadi_bay', label: 'Makadi Bay' },
  { value: 'sahl_hasheesh', label: 'Sahl Hasheesh' },
  { value: 'soma_bay', label: 'Soma Bay' },
  { value: 'el_gouna', label: 'El Gouna' },
  { value: 'safaga', label: 'Safaga' },
  { value: 'marsa_alam_airport', label: 'Marsa Alam Airport (RMF)' },
  { value: 'marsa_alam_hotels', label: 'Marsa Alam Hotels' },
  { value: 'luxor_airport', label: 'Luxor Airport (LXR)' },
  { value: 'luxor_hotels', label: 'Luxor Hotels' },
  { value: 'aswan_airport', label: 'Aswan Airport (ASW)' },
  { value: 'aswan_hotels', label: 'Aswan Hotels' },
  { value: 'cairo_airport', label: 'Cairo Airport (CAI)' },
  { value: 'cairo_hotels', label: 'Cairo Hotels' },
  { value: 'giza', label: 'Giza Pyramids' },
];

const vehicleTypes = [
  { id: 'sedan', name: 'Sedan', max_passengers: 3, max_bags: 2 },
  { id: 'suv', name: 'SUV', max_passengers: 4, max_bags: 3 },
  { id: 'minivan', name: 'Minivan', max_passengers: 7, max_bags: 5 },
  { id: 'minibus', name: 'Minibus', max_passengers: 14, max_bags: 10 },
  { id: 'limousine', name: 'Limousine', max_passengers: 4, max_bags: 2 },
];

export default function TransferForm({ isOpen, onClose, onSubmit, initialData }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    service_type: 'transfer',
    short_description: '',
    description: '',
    location: { city: 'hurghada' },
    transfer_routes: [],
    images: [{ url: '', is_main: true, caption: '' }],
    status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const [newRoute, setNewRoute] = useState({
    from: '',
    to: '',
    distance_km: 0,
    duration_min: 0,
    prices: vehicleTypes.map(vt => ({
      vehicle_type: vt.id,
      vehicle_name: vt.name,
      price: 0,
      max_passengers: vt.max_passengers,
      max_bags: vt.max_bags,
      features: ['Air conditioning', 'Free WiFi']
    }))
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        slug: initialData.slug || '',
        service_type: 'transfer',
        short_description: initialData.short_description || '',
        description: initialData.description || '',
        location: initialData.location || { city: 'hurghada' },
        transfer_routes: initialData.transfer_routes || [],
        images: initialData.images || [{ url: '', is_main: true, caption: '' }],
        status: initialData.status || 'active',
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setFormData({ ...formData, name: value, slug: generateSlug(value) });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: { ...formData[parent], [child]: value }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAddRoute = () => {
    if (newRoute.from && newRoute.to) {
      setFormData({
        ...formData,
        transfer_routes: [...formData.transfer_routes, { ...newRoute }]
      });
      setNewRoute({
        from: '',
        to: '',
        distance_km: 0,
        duration_min: 0,
        prices: vehicleTypes.map(vt => ({
          vehicle_type: vt.id,
          vehicle_name: vt.name,
          price: 0,
          max_passengers: vt.max_passengers,
          max_bags: vt.max_bags,
          features: ['Air conditioning', 'Free WiFi']
        }))
      });
    }
  };

  const handleRemoveRoute = (index) => {
    setFormData({
      ...formData,
      transfer_routes: formData.transfer_routes.filter((_, i) => i !== index)
    });
  };

  const handleRouteChange = (index, field, value) => {
    const newRoutes = [...formData.transfer_routes];
    newRoutes[index][field] = value;
    setFormData({ ...formData, transfer_routes: newRoutes });
  };

  const handlePriceChange = (routeIndex, vehicleIndex, field, value) => {
    const newRoutes = [...formData.transfer_routes];
    newRoutes[routeIndex].prices[vehicleIndex][field] = value;
    setFormData({ ...formData, transfer_routes: newRoutes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Edit Transfer Route' : 'Add New Transfer Route'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Hurghada Airport Transfers"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
              <input
                type="text"
                name="short_description"
                value={formData.short_description}
                onChange={handleChange}
                placeholder="Brief description of the transfer service"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Detailed description of the transfer service"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* Transfer Routes */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-gray-700">Transfer Routes</label>
              <button
                type="button"
                onClick={handleAddRoute}
                disabled={!newRoute.from || !newRoute.to}
                className="text-sm text-primary-500 hover:text-primary-600 flex items-center"
              >
                <FiPlus className="mr-1" /> Add Route
              </button>
            </div>

            {/* New Route Form */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <select
                    value={newRoute.from}
                    onChange={(e) => setNewRoute({ ...newRoute, from: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select pickup</option>
                    {locations.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <select
                    value={newRoute.to}
                    onChange={(e) => setNewRoute({ ...newRoute, to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select dropoff</option>
                    {locations.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Distance (km)</label>
                  <input
                    type="number"
                    value={newRoute.distance_km}
                    onChange={(e) => setNewRoute({ ...newRoute, distance_km: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={newRoute.duration_min}
                    onChange={(e) => setNewRoute({ ...newRoute, duration_min: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Existing Routes */}
            {formData.transfer_routes.map((route, routeIdx) => (
              <div key={routeIdx} className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center">
                    <FiMapPin className="text-primary-500 mr-2" />
                    <span className="font-medium">
                      {locations.find(l => l.value === route.from)?.label || route.from} → {locations.find(l => l.value === route.to)?.label || route.to}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveRoute(routeIdx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Distance (km)</label>
                    <input
                      type="number"
                      value={route.distance_km}
                      onChange={(e) => handleRouteChange(routeIdx, 'distance_km', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Duration (min)</label>
                    <input
                      type="number"
                      value={route.duration_min}
                      onChange={(e) => handleRouteChange(routeIdx, 'duration_min', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Vehicle Prices</label>
                  <div className="space-y-2">
                    {route.prices.map((price, priceIdx) => (
                      <div key={priceIdx} className="grid grid-cols-4 gap-2 items-center">
                        <span className="text-sm font-medium">{price.vehicle_name}</span>
                        <input
                          type="number"
                          placeholder="Price"
                          value={price.price}
                          onChange={(e) => handlePriceChange(routeIdx, priceIdx, 'price', parseFloat(e.target.value))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <span className="text-xs text-gray-500">Max: {price.max_passengers} pax</span>
                        <span className="text-xs text-gray-500">Bags: {price.max_bags}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
            {formData.images.map((img, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="url"
                  placeholder="Image URL"
                  value={img.url}
                  onChange={(e) => {
                    const newImages = [...formData.images];
                    newImages[idx].url = e.target.value;
                    setFormData({ ...formData, images: newImages });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Caption"
                  value={img.caption}
                  onChange={(e) => {
                    const newImages = [...formData.images];
                    newImages[idx].caption = e.target.value;
                    setFormData({ ...formData, images: newImages });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                />
                <label className="flex items-center px-3">
                  <input
                    type="checkbox"
                    checked={img.is_main}
                    onChange={(e) => {
                      const newImages = [...formData.images];
                      newImages.forEach((_, i) => newImages[i].is_main = false);
                      newImages[idx].is_main = e.target.checked;
                      setFormData({ ...formData, images: newImages });
                    }}
                    className="mr-1"
                  />
                  Main
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (formData.images.length > 1) {
                      setFormData({
                        ...formData,
                        images: formData.images.filter((_, i) => i !== idx)
                      });
                    }
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <FiTrash2 />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setFormData({
                ...formData,
                images: [...formData.images, { url: '', is_main: false, caption: '' }]
              })}
              className="text-primary-500 hover:text-primary-600 text-sm flex items-center mt-2"
            >
              <FiPlus className="mr-1" /> Add Image
            </button>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={formData.status === 'active'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Active
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={formData.status === 'inactive'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Inactive
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : initialData ? 'Update Transfer' : 'Create Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
